import { z } from 'zod';

import type { Guid } from '@apple/utils/primitive';

/** @see AppleRetail.Web.Areas.StoreEvents.Models.StoreEventCategoryModel */
export interface StoreEventCategory {
	id: Guid;
	title: string;
	imageUrl: string;
	labelImageUrl: string;
	quantity: number;
}

/** @see AppleRetail.Web.Areas.StoreEvents.ApiControllers.StoreEventCategoryApiController.StoreEventCategoryListingRequest */
export interface StoreEventCategoryListingRequest {
	currentPage: number;
	pageSize: number;
	searchText: string;
	filterOnCategories: boolean;
	categoryIds: Guid[];
}

/** @see AppleRetail.Web.Areas.StoreEvents.ApiControllers.StoreEventCategorySelectionApiController.StoreEventCategorySelectionModel */
export interface StoreEventCategorySelection {
	storeEventCategoryId: Guid;
	isLocked: boolean;
	isAwaitingSapConfirmation: boolean;
	quantity: number;
}

/** @see AppleRetail.Web.Areas.StoreEvents.ApiControllers.StoreEventCategorySelectionApiController.StoreEventCategorySelectionUpdateRequest */
export interface StoreEventCategorySelectionUpdateRequest {
	categorySelections: StoreEventCategorySelection[];
	conflictResolutions: StoreEventItemUpdateModel[];
	comment: string;
}

/** @see AppleRetail.Web.Areas.StoreEvents.ApiControllers.StoreEventCategorySelectionApiController.StoreEventItemUpdateModel */
export interface StoreEventItemUpdateModel {
	storeEventItemId: Guid | null;
	storeEventCategoryId: Guid | null;
	productItemId: string;
	quantity: number | null;
}

/** @see AppleRetail.Web.Areas.StoreEvents.ApiControllers.StoreEventCategoryApiController.StoreEventCategoryListingRequest */
export const storeEventCategoryListingRequestSchema = z.object({
	currentPage: z.number().min(1),
	pageSize: z.number().min(1),
	searchText: z.string(),
	filterOnCategories: z.boolean(),
	categoryIds: z.string().array(),
}) satisfies z.ZodType<StoreEventCategoryListingRequest>;

/** @see AppleRetail.Web.Areas.StoreEvents.ApiControllers.StoreEventCategoryApiController.StoreEventCategoryModel */
export const storeEventCategorySchema = z.object({
	id: z.string(),
	title: z.string(),
	imageUrl: z.string(),
	labelImageUrl: z.string(),
	quantity: z.number(),
}) satisfies z.ZodType<StoreEventCategory>;

/** @see AppleRetail.Web.Areas.StoreEvents.ApiControllers.StoreEventCategorySelectionApiController.StoreEventCategorySelectionModel */
export const storeEventCategorySelectionSchema = z.object({
	storeEventCategoryId: z.string().nonempty(),
	isLocked: z.boolean(),
	isAwaitingSapConfirmation: z.boolean(),
	quantity: z.number().min(1),
}) as z.ZodType<StoreEventCategorySelection>;

/** @see AppleRetail.Web.Areas.StoreEvents.ApiControllers.StoreEventCategorySelectionApiController.StoreEventItemUpdateModel */
export const storeEventItemUpdateModelSchema = z.object({
	storeEventItemId: z.string().nullable(),
	storeEventCategoryId: z.string().nullable(),
	productItemId: z.string(),
	quantity: z.number().nullable(),
}) as z.ZodType<StoreEventItemUpdateModel>;

/** @see AppleRetail.Web.Areas.StoreEvents.ApiControllers.StoreEventCategorySelectionApiController.StoreEventCategorySelectionUpdateRequest */
export const storeEventCategorySelectionUpdateRequestSchema = z.object({
	categorySelections: storeEventCategorySelectionSchema.array(),
	conflictResolutions: storeEventItemUpdateModelSchema.array(),
	comment: z.string(),
}) satisfies z.ZodType<StoreEventCategorySelectionUpdateRequest>;

export const DEFAULT_STORE_EVENT_CATEGORY_SELECTION = {
	storeEventCategoryId: '',
	size: '',
	isLocked: false,
	isAwaitingSapConfirmation: false,
	quantity: 1,
} as const as StoreEventCategorySelection;
